import { useDashboardFilters, useSectionSelection } from './useDashboardFilters'
import {
  useDashboardCard,
  useDashboardStructure,
} from '@/features/dashboard/api/queries'
import {
  MARKET_FILTER,
  TIMEFRAME_FILTER,
  type CardStub,
} from '@/features/dashboard/api/schemas'
import type { CardFilters } from '@/features/dashboard/api/client'

const ALL_TIME = 0

/**
 * A card sliced by the filters it lists: its section's timeframe and range if
 * it takes the timeframe, the selected markets if it takes the market. Other
 * charts keep the dashboard's default range, in months. Also returns the way
 * to clear the market filter, the timeframe it is drawn in and what its change
 * compares against.
 */
export const useFilteredCard = (stub: CardStub, sectionId?: string) => {
  const { data: dashboard } = useDashboardStructure()
  const { markets, clearMarkets } = useDashboardFilters()
  const { selection } = useSectionSelection(sectionId)

  const takesTimeframe = stub.filters?.includes(TIMEFRAME_FILTER) ?? false
  const filters: CardFilters = {}
  if (takesTimeframe && selection) {
    filters.timeframe = selection.timeframe
    // The range is applied to the card here (see `periodWindow`), so a
    // positive one asks for all time where it can.
    const canAskAll = dashboard?.filters?.timeframes
      ?.find(option => option.id === selection.timeframe)
      ?.periods.options.some(range => range.value === ALL_TIME)
    filters.periods =
      selection.periods > 0 && canAskAll ? ALL_TIME : selection.periods
  } else if (!takesTimeframe) {
    filters.periods = dashboard?.filters?.periods.default
  }
  if (stub.filters?.includes(MARKET_FILTER)) filters.markets = markets

  const query = useDashboardCard(stub.cardId, filters)
  return {
    ...query,
    clearMarkets,
    comparison: takesTimeframe ? selection?.comparison : undefined,
    timeframe: takesTimeframe ? selection?.timeframe : undefined,
    /** The selected range in months; none for a card not taking the timeframe. */
    months: takesTimeframe ? selection?.periods : undefined,
  }
}
